<template>
  <div></div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, watch } from 'vue';

import apiGw from '@/services/apiGw';
import { useAuthStore } from '@/store/authStore';
import { REFRESH_TOKEN_EXP } from '@/utils/general';

const REFRESH_TOKEN_BROADCAST_CHANNEL = 'refreshToken';

const authStore = useAuthStore();

const props = defineProps<{
  debug?: boolean;
}>();

interface State {
  isAuthenticated: boolean;
  refreshTokenTimer?: number;
  consecutiveFailedRefresh: number;
}

const state: State = reactive({
  isAuthenticated: computed(() => authStore.authenticated),
  refreshTokenTimer: undefined,
  consecutiveFailedRefresh: 0
});

const bc = new BroadcastChannel(REFRESH_TOKEN_BROADCAST_CHANNEL);

onMounted(() => {
  if (state.isAuthenticated) {
    bc.onmessage = (event) => {
      /**
       * Someone else (in another tab/window just refreshed the token. We first need to update the
       * refreshTokenExp with it's new value that was saved in localStorage by the other tab.
       * Then we reset the timer to match the new token expiration time.
       */
      const exp = localStorage.getItem(REFRESH_TOKEN_EXP);
      if (exp) authStore.setRefreshTokenExp(exp);
      resetRefreshTokenTimer();
    };
    initRefreshTokenTimer();
  }
});

onBeforeUnmount(() => {
  cancelRefreshTokenTimer();
  bc.close();
});

watch(
  () => state.isAuthenticated,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      initRefreshTokenTimer();
    } else if (oldVal && !newVal) {
      cancelRefreshTokenTimer();
    }
  }
);

function resetRefreshTokenTimer() {
  cancelRefreshTokenTimer();
  initRefreshTokenTimer();
}

function cancelRefreshTokenTimer() {
  clearInterval(state.refreshTokenTimer);
  state.refreshTokenTimer = undefined;
}

function initRefreshTokenTimer() {
  if (state.refreshTokenTimer) return;

  state.consecutiveFailedRefresh = 0;
  const exp = authStore.refreshTokenExp;
  if (props.debug) console.log('YapRefreshToken.vue->initRefreshTokenTimer | exp', exp);

  if (!exp) return; // no expiration retrieved from localStorage
  const now = new Date().getTime();
  const expTime = new Date(exp).getTime();
  if (expTime - now <= 0) {
    // retrieved expiration time has already passed
    authStore.removeRefreshTokenExp();
    return;
  }

  const rand = Math.random() * 10_000; // random value used to avoid multiple tabs refreshing the token at the same time
  let expDelta = expTime - now - rand - 180_000; // 3min before token expiration
  if (props.debug)
    console.log(`   refreshTokenTimer set to ${expDelta.toFixed(2)}ms or ${(expDelta / 1000 / 60).toFixed(2)}min`);
  if (expDelta < 3000) {
    if (props.debug) console.log(`   expDelta: ${expDelta} < 3000 => setting to refreshTokenTimer 3000ms`);
    expDelta = 3000;
  }
  state.refreshTokenTimer = window.setInterval(() => refreshToken(), expDelta);
}

async function refreshToken() {
  if (state.consecutiveFailedRefresh > 5) {
    console.warn('Could not refresh token 5 times in a row... cancelling refreshToken');
    cancelRefreshTokenTimer();
    return;
  }
  try {
    if (props.debug) console.warn(`Firing refresh token request @ ${new Date().toISOString()}`);
    const rsp = await apiGw.post('/v3/auth/refresh');
    const tokenExpirationTime = rsp.headers['x-refresh-token-exp'];
    if (tokenExpirationTime) {
      authStore.setRefreshTokenExp(tokenExpirationTime);
    }
    bc.postMessage({ time: new Date().getTime(), msg: 'Token refreshed' });
    /**
     * Make sure we update the refreshToken timer taking into account the new expiration time
     */
    resetRefreshTokenTimer();
    state.consecutiveFailedRefresh = 0;
  } catch (error) {
    console.error('Something when wrong while calling refreshToken', error);
    state.consecutiveFailedRefresh++;
  }
}
</script>
